/**
 * decide(state, command, ctx) → events | rejection. Pure: no clock, no randomness (both arrive in ctx). Shared by
 * the Project DO, the web client (optimistic updates, ghost nodes), agent dry runs and tests (§0, §17).
 *
 * The DO pipeline (§5.2) around it: decode → stamp → dedupe (`checkDedupe`) → fence + authorize + preconditions +
 * policy + decide (`decideEnvelope`) → append (`stampEvents` / `commit`) → respond (`resultOf`) → reactions.
 */
import { allowRulesFor } from "@minga/protocol"
import { authorize } from "../authorize"
import { evolve } from "../evolve"
import { canonicalJson, sha256Hex } from "../hash"
import { must, recordOf } from "../state"
import type {
  Actor,
  Command,
  CommandEnvelope,
  CommandRejection,
  CommandResult,
  EventMeta,
  Ids,
  PolicyStamp,
  ProjectEvent,
  ProjectEventEnvelope,
  ProjectState,
} from "../types"
import { revertPlan, submitChangeset, undoChangeset } from "./changeset"
import {
  cancelProjectWork,
  linkProjectWork,
  receiveProjectReply,
  receiveProjectWork,
  receiveWorkControl,
  recordWorkDelivery,
  replyProjectWork,
  requestProjectWork,
  verifyProjectWork,
} from "./coordination"
import { recordPrState, refreshReviewCards } from "./github"
import {
  answer,
  ask,
  decideFlow,
  editComment,
  escalateStall,
  holdWake,
  lendRunner,
  noteOverdue,
  postArtifact,
  postComment,
  recordBetVerdict,
  recordKpiSample,
  recordKpiSamples,
  recordReview,
  resolveThread,
  sendBrief,
  setArtifactState,
} from "./items"
import {
  applyRunnerGrants,
  closeCoveredPermissions,
  closeCoveredPlanCards,
  decideProposal,
  escalateProposal,
  expireProposal,
  openProposalCmd,
  reviseProposal,
  withdrawProposal,
} from "./ledger"
import { proposeMemory, writeMemory } from "./memory"
import { markMessagesRead, recordMessageDelivery, sendMessage } from "./messages"
import {
  fireMonitor,
  noteLooseEnds,
  noteRunQuiet,
  noteSeriesDue,
  removeMonitor,
  resumeMonitor,
  setMonitor,
  setMonitorPaused,
} from "./monitors"
import {
  cancelRun,
  raiseRunTaint,
  recordRunEnded,
  recordRunLeased,
  recordRunMilestone,
  recordRunRequeued,
  recordRunStarted,
  recordRunWaiting,
  reportOutcome,
  reportVerdict,
  requestRun,
} from "./runs"
import { recordAskWaiting, settleShareCards } from "./share-login"
import {
  createPolicy,
  createProject,
  grantIntegration,
  recordOutwardResult,
  requestOutwardAction,
  revisePolicy,
  revokeIntegration,
  setHold,
  syncWorkspace,
  updateProject,
  upgradePolicies,
  upgradePolicyPresets,
} from "./steering"
import { type Annotation, type DecideContext, EVENT_VERSION, Rejected, sameActor, Tx } from "./tx"

export type { Annotation, DecideContext, DecideHistory } from "./tx"

export type Decision =
  | {
      readonly _tag: "Emit"
      readonly events: ReadonlyArray<ProjectEvent>
      /** Index-aligned with `events`: actor, changeset, policy stamp and approval of each event. */
      readonly annotations: ReadonlyArray<Annotation>
      /** The policy stamps of the graph changes that were applied (for plan-diff cards and DryRun). */
      readonly policy: ReadonlyArray<PolicyStamp>
    }
  | { readonly _tag: "Reject"; readonly rejection: CommandRejection }

const dispatch = (tx: Tx, c: Command): void => {
  switch (c._tag) {
    case "SubmitChangeset":
      submitChangeset(tx, c)
      return
    case "UndoChangeset":
      undoChangeset(tx, c)
      return
    case "RevertPlan":
      revertPlan(tx, c)
      return
    case "OpenProposal":
      openProposalCmd(tx, c)
      return
    case "ReviseProposal":
      reviseProposal(tx, c)
      return
    case "WithdrawProposal":
      withdrawProposal(tx, c)
      return
    case "DecideProposal":
      decideProposal(tx, c)
      return
    case "EscalateProposal":
      escalateProposal(tx, c)
      return
    case "ExpireProposal":
      expireProposal(tx, c)
      return
    case "SetHold":
      setHold(tx, c)
      return
    case "UpdateProject":
      updateProject(tx, c)
      // decision 0138: a saved allowlist closes the open permission cards it now covers
      if (c.patch.allow !== undefined)
        closeCoveredPermissions(
          tx,
          allowRulesFor(tx.state.project?.allow ?? [], null),
          "covered by the project's allowed list",
        )
      return
    case "CreatePolicy":
      createPolicy(tx, c)
      return
    case "RevisePolicy":
      revisePolicy(tx, c)
      // decision 0138: open plan cards the new rules cover apply themselves
      closeCoveredPlanCards(tx, `covered by policy rev ${c.revision + 1}`)
      return
    case "ApplyRunnerGrants":
      applyRunnerGrants(tx, c)
      return
    case "UpgradePolicies":
      upgradePolicies(tx, c)
      return
    case "UpgradePolicyPresets":
      upgradePolicyPresets(tx, c)
      return
    case "GrantIntegration":
      grantIntegration(tx, c)
      return
    case "RevokeIntegration":
      revokeIntegration(tx, c)
      return
    case "RequestRun":
      requestRun(tx, c)
      return
    case "CancelRun":
      cancelRun(tx, c)
      return
    case "LendRunner":
      // wave E (decision 0063): the runner's owner lends it to one item, or anyone ends the lend
      lendRunner(tx, c)
      return
    case "RecordRunWaiting":
      recordRunWaiting(tx, c)
      return
    case "RecordAskWaiting":
      // re-walk 0166 §2: a queued message's turn, probed: its share cards now
      recordAskWaiting(tx, c)
      return
    case "RecordRunRequeued":
      recordRunRequeued(tx, c)
      return
    case "RecordRunLeased":
      recordRunLeased(tx, c)
      return
    case "RecordRunStarted":
      recordRunStarted(tx, c)
      return
    case "RecordRunMilestone":
      recordRunMilestone(tx, c)
      return
    case "RecordPrState":
      recordPrState(tx, c)
      return
    case "ReportOutcome":
      reportOutcome(tx, c)
      return
    case "ReportVerdict":
      reportVerdict(tx, c)
      return
    case "RaiseRunTaint":
      raiseRunTaint(tx, c)
      return
    case "RecordRunEnded":
      recordRunEnded(tx, c)
      return
    case "RecordReview":
      recordReview(tx, c)
      return
    case "PostComment":
      postComment(tx, c)
      return
    case "EditComment":
      editComment(tx, c)
      return
    case "ResolveThread":
      resolveThread(tx, c)
      return
    case "Ask":
      ask(tx, c)
      return
    case "Answer":
      answer(tx, c)
      return
    case "SendMessage":
      sendMessage(tx, c)
      return
    case "MarkMessagesRead":
      markMessagesRead(tx, c)
      return
    case "RecordMessageDelivery":
      recordMessageDelivery(tx, c)
      return
    case "PostArtifact":
      postArtifact(tx, c)
      return
    case "SetArtifactState":
      setArtifactState(tx, c)
      return
    case "ProposeMemory":
      proposeMemory(tx, c)
      return
    case "WriteMemory":
      writeMemory(tx, c)
      return
    case "RequestOutwardAction":
      requestOutwardAction(tx, c)
      return
    case "RecordOutwardResult":
      recordOutwardResult(tx, c)
      return
    case "NoteOverdue":
      noteOverdue(tx, c)
      return
    case "RefreshReviewCards":
      refreshReviewCards(tx, c)
      return
    case "SendBrief":
      sendBrief(tx, c)
      return
    case "RecordKpiSample":
      recordKpiSample(tx, c)
      return
    case "RecordKpiSamples":
      recordKpiSamples(tx, c)
      return
    case "RecordBetVerdict":
      recordBetVerdict(tx, c)
      return
    case "EscalateStall":
      escalateStall(tx, c)
      return
    case "HoldWake":
      holdWake(tx, c)
      return
    case "SyncWorkspace":
      syncWorkspace(tx, c)
      return
    case "CreateProject":
      createProject(tx, c)
      return
    case "RetryItem":
    case "UnblockItem":
    case "AcceptImpossible":
    case "PauseItem":
      decideFlow(tx, c)
      return
    // wave C (decision 0071): monitors and the clock
    case "SetMonitor":
      setMonitor(tx, c)
      return
    case "RequestProjectWork":
      requestProjectWork(tx, c)
      return
    case "CancelProjectWork":
      cancelProjectWork(tx, c)
      return
    case "LinkProjectWork":
      linkProjectWork(tx, c)
      return
    case "ReceiveWorkControl":
      receiveWorkControl(tx, c)
      return
    case "ReceiveProjectWork":
      receiveProjectWork(tx, c)
      return
    case "ReplyProjectWork":
      replyProjectWork(tx, c)
      return
    case "VerifyProjectWork":
      verifyProjectWork(tx, c)
      return
    case "ReceiveProjectReply":
      receiveProjectReply(tx, c)
      return
    case "RecordWorkDelivery":
      recordWorkDelivery(tx, c)
      return
    case "RemoveMonitor":
      removeMonitor(tx, c)
      return
    case "SetMonitorPaused":
      setMonitorPaused(tx, c)
      return
    case "ResumeMonitor":
      resumeMonitor(tx, c)
      return
    case "FireMonitor":
      fireMonitor(tx, c)
      return
    case "NoteLooseEnds":
      noteLooseEnds(tx, c)
      return
    case "NoteSeriesDue":
      noteSeriesDue(tx, c)
      return
    case "NoteRunQuiet":
      noteRunQuiet(tx, c)
      return
  }
}

/** Authorize (actor kind), then run the handler in a scratch transaction. A rejection writes no event. */
export const decide = (state: ProjectState, command: Command, ctx: DecideContext): Decision => {
  const denied = authorize(state, ctx.actor, command)
  if (denied) return { _tag: "Reject", rejection: denied }
  const tx = new Tx(state, ctx)
  try {
    dispatch(tx, command)
    // 0128 addendum: a share card no waiting turn backs any more (shared, ran, ended) goes away
    settleShareCards(tx)
  } catch (e) {
    if (e instanceof Rejected) return { _tag: "Reject", rejection: e.rejection }
    throw e
  }
  const policy = tx.annotations
    .map((a, i) =>
      a.changesetId !== null && a.policy !== null && tx.events[i]?._tag !== "ChangesetApplied"
        ? a.policy
        : null,
    )
    .filter((p): p is PolicyStamp => p !== null)
  return { _tag: "Emit", events: tx.events, annotations: tx.annotations, policy }
}

/** Optimistic preconditions on exactly the records the command depends on (§5.1). */
export const checkPreconditions = (
  state: ProjectState,
  envelope: Pick<CommandEnvelope, "expect">,
): CommandRejection | null => {
  for (const e of envelope.expect ?? []) {
    const rec = recordOf(state, e.ref)
    if (!rec) return { _tag: "NotFound", ref: e.ref }
    if (rec.lastSeq !== e.lastSeq)
      return {
        _tag: "PreconditionFailed",
        ref: e.ref,
        expected: e.lastSeq,
        actual: rec.lastSeq as Ids["Seq"],
      }
  }
  return null
}

/** Pipeline steps 5–8 for one envelope: authorize, preconditions, policy + decide. */
export const decideEnvelope = (
  state: ProjectState,
  envelope: CommandEnvelope,
  ctx: DecideContext,
): Decision => {
  const denied = authorize(state, ctx.actor, envelope.command)
  if (denied) return { _tag: "Reject", rejection: denied }
  const pre = checkPreconditions(state, envelope)
  if (pre) return { _tag: "Reject", rejection: pre }
  return decide(state, envelope.command, ctx)
}

/** Stamp full metadata onto emitted events (pipeline step 9). Seqs follow `state.seq`. */
export const stampEvents = (
  state: ProjectState,
  decision: Extract<Decision, { _tag: "Emit" }>,
  ctx: DecideContext,
  envelope: Pick<CommandEnvelope, "commandId" | "causedBy" | "links"> | null,
  taintOf: (actor: Actor) => EventMeta["taint"] = () => "clean",
): ReadonlyArray<ProjectEventEnvelope> => {
  // taint is the actor's run taint at append time: replay the scratch fold to read it per event
  let s = state
  return decision.events.map((event, i) => {
    const a = must(decision.annotations[i], "annotation")
    const runTaint =
      a.actor._tag === "agent" || a.actor._tag === "worker"
        ? (s.runs.find((r) => r.id === (a.actor as { runId: string }).runId)?.taint ?? taintOf(a.actor))
        : taintOf(a.actor)
    const meta: EventMeta = {
      seq: (state.seq + i + 1) as Ids["Seq"],
      ts: ctx.now,
      v: EVENT_VERSION,
      actor: a.actor,
      commandId: envelope?.commandId ?? null,
      causedBy: envelope?.causedBy ?? null,
      links: envelope?.links ?? [],
      correlationId: ctx.correlationId,
      changesetId: a.changesetId,
      policy: a.policy,
      approvedBy: a.approvedBy,
      taint: runTaint,
    }
    s = evolve(s, event, meta)
    return { ...meta, event }
  })
}

/** Decide and fold in one step: the new state, the stamped events and the result. */
export const commit = (
  state: ProjectState,
  envelope: CommandEnvelope,
  ctx: DecideContext,
): {
  readonly state: ProjectState
  readonly events: ReadonlyArray<ProjectEventEnvelope>
  readonly result: CommandResult
} => {
  const d = decideEnvelope(state, envelope, ctx)
  if (d._tag === "Reject") return { state, events: [], result: { _tag: "Rejected", rejection: d.rejection } }
  const events = stampEvents(state, d, ctx, envelope)
  const next = events.reduce((s, { event, ...meta }) => evolve(s, event, meta), state)
  return { state: next, events, result: resultOf(state, events, ctx.actor) }
}

/**
 * The CommandResult for appended events: `Proposed` whenever part of the command became the actor's own proposal
 * (with what was applied alongside under apply_allowed), else `Applied`.
 */
export const resultOf = (
  before: ProjectState,
  events: ReadonlyArray<ProjectEventEnvelope>,
  actor: Actor,
): CommandResult => {
  const cs = events.filter((e) => e.event._tag === "ChangesetApplied")
  const lastCs = cs[cs.length - 1]
  const planVersion = lastCs && lastCs.event._tag === "ChangesetApplied" ? lastCs.event.planVersion : null
  const own = events.find(
    (e) => e.event._tag === "ProposalOpened" && sameActor(e.event.proposal.proposedBy, actor),
  )
  const firstSeq = (events[0]?.seq ?? before.seq) as Ids["Seq"]
  if (own && own.event._tag === "ProposalOpened") {
    const appliedEvents = events.filter((e) => e.seq < own.seq && e.changesetId !== null)
    return {
      _tag: "Proposed",
      proposalId: own.event.proposal.id,
      seq: own.seq,
      applied:
        appliedEvents.length > 0 && planVersion !== null
          ? {
              firstSeq: must(appliedEvents[0], "event").seq,
              lastSeq: must(appliedEvents[appliedEvents.length - 1], "event").seq,
              planVersion,
            }
          : null,
    }
  }
  return {
    _tag: "Applied",
    firstSeq,
    lastSeq: (events[events.length - 1]?.seq ?? before.seq) as Ids["Seq"],
    planVersion,
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Transport idempotency (§14): (actorKey, idempotencyKey ?? commandId) → the original result; a different body → Conflict.

/** Scope of a dedupe key: user, agent+run, worker+run(+epoch), runner, system process. */
export const actorKey = (actor: Actor, epoch?: number): string => {
  switch (actor._tag) {
    case "user":
      return `user:${actor.userId}`
    case "agent":
      return `agent:${actor.agentId}:${actor.runId}`
    case "worker":
      return `worker:${actor.runId}:${epoch ?? 0}`
    case "runner":
      return `runner:${actor.runnerId}`
    case "system":
      return `system:${actor.process}`
  }
}

export const dedupeKey = (envelope: Pick<CommandEnvelope, "commandId" | "idempotencyKey">): string =>
  envelope.idempotencyKey ?? envelope.commandId

/**
 * Hash of the command body (what must match for a replay to be "the same command"). Prefixed `v2:` since the
 * work-model cutover (decision 0036): a stored hash without the prefix was written before it.
 */
export const bodyHash = (envelope: Pick<CommandEnvelope, "command">): string =>
  `${BODY_HASH_PREFIX}${sha256Hex(canonicalJson(envelope.command))}`

export const BODY_HASH_PREFIX = "v2:"

export interface DedupeRow {
  readonly bodyHash: string
  readonly result: CommandResult
}

export type DedupeCheck =
  | { readonly _tag: "fresh"; readonly key: string; readonly bodyHash: string }
  | { readonly _tag: "replay"; readonly result: CommandResult }
  | { readonly _tag: "conflict"; readonly rejection: CommandRejection }

/**
 * Pipeline step 3 against the DO's command_dedupe table (passed in as a lookup; its result decoded through
 * `decodeStored.commandResult`). A key hit on a row written before the cutover (no `v2:` prefix) is a replay whatever
 * the new body: retries across the deploy rebuild v2 bodies for v1 commands (an alias's CloseItem for a CompleteTask,
 * minted `itm_` ids), and the stored v1 hash could never match them. Prefixed rows keep the strict comparison.
 */
export const checkDedupe = (
  lookup: (actorKey: string, key: string) => DedupeRow | undefined,
  actor: Actor,
  envelope: Pick<CommandEnvelope, "commandId" | "idempotencyKey" | "command">,
  epoch?: number,
): DedupeCheck => {
  const ak = actorKey(actor, epoch)
  const key = dedupeKey(envelope)
  const h = bodyHash(envelope)
  const row = lookup(ak, key)
  if (!row) return { _tag: "fresh", key: `${ak}|${key}`, bodyHash: h }
  if (row.bodyHash.startsWith(BODY_HASH_PREFIX) && row.bodyHash !== h)
    return {
      _tag: "conflict",
      rejection: { _tag: "Conflict", reason: `idempotency key ${key} was used with a different body` },
    }
  const r = row.result
  if (r._tag === "Applied" || r._tag === "Proposed")
    return { _tag: "replay", result: { _tag: "Deduplicated", original: r } }
  return { _tag: "replay", result: r }
}
